import { Obj } from "../internal";
import { ArcgisLayer, FrameFeatureLayer, Infra3DLayer, Layer, LAYERMODE, MemoryLayer, NetworkLayer, StrategyHandler, WFSGeoserverLayer, WFSQGISLayer, WFS20, } from "./internal";
export var LAYERTYPE;
(function (LAYERTYPE) {
    LAYERTYPE[LAYERTYPE["BASE"] = 0] = "BASE";
    LAYERTYPE[LAYERTYPE["INFRA3DLAYER"] = 1] = "INFRA3DLAYER";
    LAYERTYPE[LAYERTYPE["WFSLAYER"] = 2] = "WFSLAYER";
    LAYERTYPE[LAYERTYPE["MEMORYLAYER"] = 3] = "MEMORYLAYER";
    LAYERTYPE[LAYERTYPE["ARCGISLAYER"] = 4] = "ARCGISLAYER";
    LAYERTYPE[LAYERTYPE["FRAMEFEATURELAYER"] = 5] = "FRAMEFEATURELAYER";
    LAYERTYPE[LAYERTYPE["NETWORKLAYER"] = 6] = "NETWORKLAYER";
    LAYERTYPE[LAYERTYPE["WFSQGISLAYER"] = 7] = "WFSQGISLAYER";
    LAYERTYPE[LAYERTYPE["WFS20"] = 8] = "WFS20";
})(LAYERTYPE || (LAYERTYPE = {}));
/**
 * Manages layers set on the scene
 * @class LayerHandler
 */
export class LayerHandler extends Obj {
    constructor(scene) {
        super();
        this.layers = [];
        /**
         * Strategy-Handler
         * @type {StrategyHandler}
         */
        this.strategyhandler = null;
        /**
         * One currently active layer.
         * @type {Layer}
         */
        this.activelayer = null;
        /**
         * this callback is called when the active layer changes
         * @property ononlayerchanged
         */
        this.onlayerchanged = null;
        /**
         * called when a layer was added
         */
        this.onlayeradded = null;
        /**
         * called when a layer was removed
         * @type {any}
         */
        this.onlayerremoved = null;
        this.scene = scene;
        this.strategyhandler = new StrategyHandler(this.scene);
    }
    /**
     * Add a layer to the scene.
     * @param layertype
     * @param opt_options
     * @returns {Layer}
     */
    addLayer(layertype, opt_options) {
        let layer;
        switch (layertype) {
            case LAYERTYPE.BASE:
                layer = new Layer(this.scene, opt_options);
                break;
            case LAYERTYPE.INFRA3DLAYER:
                layer = new Infra3DLayer(this.scene, opt_options);
                break;
            case LAYERTYPE.WFSLAYER:
                layer = new WFSGeoserverLayer(this.scene, opt_options);
                break;
            case LAYERTYPE.MEMORYLAYER:
                layer = new MemoryLayer(this.scene, opt_options);
                break;
            case LAYERTYPE.ARCGISLAYER:
                layer = new ArcgisLayer(this.scene, opt_options);
                break;
            case LAYERTYPE.FRAMEFEATURELAYER:
                layer = new FrameFeatureLayer(this.scene, opt_options);
                break;
            case LAYERTYPE.NETWORKLAYER:
                layer = new NetworkLayer(this.scene, opt_options);
                break;
            case LAYERTYPE.WFSQGISLAYER:
                layer = new WFSQGISLayer(this.scene, opt_options);
                break;
            case LAYERTYPE.WFS20:
                layer = new WFS20(this.scene, opt_options);
                break;
            default:
                throw new Error("Layer type not supported: " + layertype);
        }
        this.layers.push(layer);
        if (this.onlayeradded != null)
            this.onlayeradded(layer);
        //registers the layer on the strategy-handler
        this.strategyhandler.register(layer);
        return layer;
    }
    /**
     * Removes a layer form the scene
     * @param layer
     */
    removeLayer(layer) {
        for (let i = 0; i < this.layers.length; i++) {
            if (this.layers[i] === layer)
                this.layers.splice(i, 1);
        }
        this.strategyhandler.remove(layer);
        layer.destroy();
        if (this.onlayerremoved != null)
            this.onlayerremoved(layer);
        if (layer === this.activelayer)
            this.setActiveLayer(null);
    }
    /**
     * Set the active layer
     * @method setActiveLayer
     * @param {Layer} layer
     */
    setActiveLayer(layer) {
        if (this.activelayer === layer)
            return;
        this.activelayer = layer;
        if (this.onlayerchanged != null)
            this.onlayerchanged(this.activelayer);
    }
    /**
     * Get the active layer
     * @method getActiveLayer
     * @return {Layer}
     */
    getActiveLayer() {
        return this.activelayer;
    }
    /**
     * Get layer by Name
     * @method getLayersByName
     * @return {Layer}
     */
    getLayersByName(layername) {
        const ret_layers = [];
        for (let i = 0; i < this.layers.length; i++) {
            if (this.layers[i].layername == layername) {
                ret_layers.push(this.layers[i]);
            }
        }
        return ret_layers;
    }
    /**
     * Get layer by FeatureCollection
     * @method getLayerByFeatureCollection
     * @return {Layer}
     */
    getLayerByFeatureCollection(fc) {
        for (let i = 0; i < this.layers.length; i++) {
            if (this.layers[i].getFeatureCollection() === fc) {
                return this.layers[i];
            }
            if (this.layers[i].mode == LAYERMODE.EDIT) {
                if (this.layers[i].getLayerEditor().featuresinsert === fc) {
                    return this.layers[i];
                }
            }
        }
        return null;
    }
    /**
     * Get layers by Topic
     * @method getLayersByTopic
     * @return {Layer[]}
     */
    getLayersByTopic(topic) {
        const retlayers = [];
        for (let i = 0; i < this.layers.length; i++) {
            if (this.layers[i].topic == topic) {
                retlayers.push(this.layers[i]);
            }
        }
        return retlayers;
    }
    getNetworkLayerByAxis(axis) {
        for (const layer of this.layers) {
            if (layer.type == LAYERTYPE.NETWORKLAYER) {
                const networklayer = layer;
                for (const iaxis of networklayer.getAxisCollection()) {
                    if (iaxis.axis === axis)
                        return networklayer;
                }
            }
        }
        return null;
    }
    /**
     * Sets the OnLayerChanged-Callback-Function
     * @method setOnLayerChanged
     * @param {Function} f
     */
    setOnLayerChanged(f) {
        this.onlayerchanged = f;
    }
    /**
     * Sets the OnLayerAdded-Callback-Function
     * @method setOnLayerAdded
     * @param {Function} f
     */
    setOnLayerAdded(f) {
        this.onlayeradded = f;
    }
    /**
     * Sets the OnLayerRemoved-Callback-Function
     * @method setOnLayerRemoved
     * @param {Function} f
     */
    setOnLayerRemoved(f) {
        this.onlayerremoved = f;
    }
    showLayerById(layerId) {
        const layer = this.layers.find((layer) => layer.id === layerId);
        if (!layer) {
            throw new Error(`Layer with id "${layerId}" not found.`);
        }
        layer.setVisible(true);
    }
    hideLayerById(layerId) {
        const layer = this.layers.find((layer) => layer.id === layerId);
        if (!layer) {
            throw new Error(`Layer with id "${layerId}" not found.`);
        }
        layer.setVisible(false);
    }
}
//# sourceMappingURL=layerhandler.js.map